import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  CircleAlert,
  Copy,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Repeat,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  getAgencyPayerEnrollments,
  refreshAgencyEnrollments,
  type AgencyClinicResponse,
  type AgencyInsuranceResponse,
  type PayerEnrollmentGroup,
  type PayerEnrollmentsResponse,
} from '../../services/clinicsApi';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { getAgencyClinicDisplayName } from '../../utils/agencyClinicDisplayName';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { EnrollmentFilters } from './EnrollmentFilters';
import { InsuranceHeader, InsuranceLine } from './InsuranceHeader';
import { isWellSkySyncedInsurance } from './insuranceUtils';
import { PayerEnrollmentsTable } from './PayerEnrollmentsTable';
import { PayerName } from './PayerName';
import { PayerSteps } from './PayerSteps';
import { RecurringEligibilityDialog } from './RecurringEligibilityDialog';
import { StartEnrollmentsDialog } from './StartEnrollmentsDialog';
import { SupportIcon } from './SupportIcon';
import {
  PAYER_TRANSACTION_LABELS,
  agencyTitle,
  clinicLabel,
  clinicNameFrom,
  linesInView,
  markLabel,
  countOutstanding,
  enrollmentTodos,
  splitRetired,
  summaryRank,
  transactionStates,
  transactionsInView,
  withClinicNames,
  type PayerTransaction,
} from './payerEnrollments';

/** One insurance, with the payer that pays it where there is one. */
interface InsuranceRow {
  insurance: AgencyInsuranceResponse;
  payer: PayerEnrollmentGroup | null;
  /** The clinic's display name, shown when more than one clinic is in view. */
  clinicName: string | null;
}

interface EnrollmentsTabProps {
  canRefresh: boolean;
  /**
   * Report the outstanding count back to Admin, which owns the tab badge.
   *
   * Admin fetches it once on mount; without this the badge would keep that
   * first answer while somebody watches the table change under a Refresh.
   * Need not be referentially stable — it is held in a ref.
   */
  onCountChange?: (count: number) => void;
  /** The agency's insurances, one row each. */
  insurances: AgencyInsuranceResponse[];
  /** Admin's clinics, for each row's clinic name. */
  agencyClinics: AgencyClinicResponse[];
  /** Absent when the person cannot add an insurance. */
  onAddInsurance?: () => void;
  /** Set when Add is shown but off, saying why; shown on hover. */
  addInsuranceBlockedReason?: string | null;
  /** The copy-to-clinic tool; super users only, so it sits in the menu. */
  onCopyInsurances?: () => void;
  /** Absent when the person can only view insurances. */
  onEdit?: (insurance: AgencyInsuranceResponse) => void;
  onEditRates?: (insurance: AgencyInsuranceResponse) => void;
}

// The tab is visible to anyone with the Insurance permission, but the endpoint
// needs access to every clinic in the agency. Deliberately no per-clinic
// fallback: an admin limited to some clinics sees this message and no
// insurance list, rather than a partial list that reads as the whole agency.
// Says that some clinics are out of reach, never which ones.
const NO_CLINIC_ACCESS_MESSAGE =
  'Enrollments covers every clinic in the agency, and you don’t have access to all of them.';

/**
 * Admin > Insurance: one row per payer, the way Stedi's payer network table
 * lays it out, each opening to every clinic's enrollments with that payer.
 */
export function EnrollmentsTab({
  canRefresh,
  onCountChange,
  insurances,
  agencyClinics,
  onAddInsurance,
  addInsuranceBlockedReason,
  onCopyInsurances,
  onEdit,
  onEditRates,
}: EnrollmentsTabProps) {
  // Held in a ref, not read from the closure: `load` runs from an effect keyed
  // on its own identity, so a caller passing an inline arrow would otherwise
  // give it a new identity every render and refetch forever.
  const countCallback = useRef(onCountChange);
  useEffect(() => {
    countCallback.current = onCountChange;
  }, [onCountChange]);
  const { showError, hasPermission, agencyKinnserEnabled } = useStore(
    useShallow(state => ({
      showError: state.showError,
      hasPermission: state.hasPermission,
      agencyKinnserEnabled: state.user?.agency?.is_kinnser_enabled,
    }))
  );
  // Roger staff only: starting enrollments submits to payers for real.
  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);
  const [startOpen, setStartOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [data, setData] = useState<PayerEnrollmentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Kept separately from `data` because the two failures look different: a
  // first load leaves no rows at all, a failed refresh leaves the previous
  // ones on screen. Either way the table must not be the thing that answers,
  // because an empty table here reads as "nothing outstanding" — the exact
  // wrong answer to give wrongly on a tab whose job is chasing deadlines.
  const [loadFailed, setLoadFailed] = useState(false);
  // Separate from `loadFailed` because retrying is the wrong offer: the
  // endpoint is agency-wide and this caller cannot see every clinic, so the
  // answer will not change until somebody grants them the rest.
  const [missingClinicAccess, setMissingClinicAccess] = useState(false);

  const [search, setSearch] = useState('');
  const [clinicIds, setClinicIds] = useState<string[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<
    PayerTransaction[]
  >([]);
  const transactions = useMemo(
    () => transactionsInView(selectedTransactions),
    [selectedTransactions]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const loaded = await getAgencyPayerEnrollments();
      setData(loaded);
      // The same statuses the count endpoint counts, from the rows already in
      // hand rather than asking again.
      countCallback.current?.(countOutstanding(loaded.payers ?? []));
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to load enrollments', error);
      if (getHttpStatus(error) === 403) {
        showError(NO_CLINIC_ACCESS_MESSAGE);
        setMissingClinicAccess(true);
      } else {
        showError('Unable to load enrollments. Please try again.');
      }
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  // The rows join two loads: the insurances (owned by the Insurance section)
  // and the enrollments here. An insurance added, edited or deleted changes
  // which payer it falls under, so reload the enrollments when the list
  // changes; skip the first, which the load above already covers.
  const seenInsurances = useRef(insurances);
  useEffect(() => {
    if (seenInsurances.current === insurances) return;
    seenInsurances.current = insurances;
    void load();
  }, [insurances, load]);

  // After Start enrollments the new rows are already stored, so a failed
  // Stedi refresh must not hide them behind the refresh error.
  const handleStarted = async () => {
    try {
      await refreshAgencyEnrollments();
    } catch (error) {
      console.error('Failed to refresh enrollments after start', error);
    }
    await load();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await refreshAgencyEnrollments();
      await load();
      if (result.clinics_failed > 0) {
        // The sync takes a savepoint per clinic, so some can fail while the
        // rest are written and the call still answers 200. A clearinghouse
        // outage is a 502 and lands in the catch below instead.
        showError(
          `Could not refresh ${result.clinics_failed} of ${
            result.clinics_failed + result.clinics_refreshed
          } clinics. Please try again.`
        );
      }
    } catch (error) {
      console.error('Failed to refresh enrollments', error);
      if (getHttpStatus(error) === 403) {
        showError(NO_CLINIC_ACCESS_MESSAGE);
        setMissingClinicAccess(true);
      } else {
        showError('Unable to refresh enrollments. Please try again.');
      }
      setLoadFailed(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const clinicNames = useMemo(
    () =>
      new Map(
        agencyClinics.map(clinic => [
          clinic.id,
          getAgencyClinicDisplayName(clinic, {
            totalClinicCount: agencyClinics.length,
          }),
        ])
      ),
    [agencyClinics]
  );

  const payers = useMemo(
    () => withClinicNames(data?.payers ?? [], clinicNames),
    [data, clinicNames]
  );
  // Clinic lines whose insurances are all retired in WellSky leave the payer
  // rows; a payer left with none in view moves to the Retired group.
  const { live: livePayers, retired: retiredParts } = useMemo(
    () =>
      splitRetired(
        payers,
        new Set(
          insurances
            .filter(insurance => insurance.retired_in_wellsky)
            .map(insurance => insurance.id)
        )
      ),
    [payers, insurances]
  );
  const clinics = useMemo(
    () =>
      (data?.clinics ?? []).map(clinic => ({
        ...clinic,
        name: clinicNameFrom(clinicNames, clinic.id, clinic.name),
      })),
    [data, clinicNames]
  );

  // One line per kind of gap, with the clinics listed after it. Six clinics
  // with an unverifiable NPI were six near-identical sentences in one long
  // run, and the repetition hid how few distinct problems there are.
  const blindSpots = useMemo(() => {
    const parts: string[] = [];
    const withoutNpi = data?.clinics_without_npi ?? [];
    if (withoutNpi.length > 0) {
      parts.push(`No NPI: ${withoutNpi.join(', ')}`);
    }
    const unverifiable = data?.clinics_with_unverifiable_npi ?? [];
    if (unverifiable.length > 0) {
      // Deliberately vague about the cause. The agency cannot act on this and
      // the cause involves another tenant, so it says the table is incomplete
      // and nothing more.
      parts.push(`NPI couldn't be verified: ${unverifiable.join(', ')}`);
    }
    return parts;
  }, [data]);

  // Which payer pays each insurance, read off the clinic lines under it.
  const payerByInsurance = useMemo(() => {
    const map = new Map<string, PayerEnrollmentGroup>();
    for (const payer of payers) {
      for (const line of payer.clinics ?? []) {
        for (const insurance of line.insurances ?? []) {
          map.set(insurance.id, payer);
        }
      }
    }
    return map;
  }, [payers]);

  // One row per insurance in view, needing action first, then by name.
  const rows = useMemo<InsuranceRow[]>(() => {
    const needle = search.trim().toLowerCase();
    return insurances
      .filter(
        insurance =>
          clinicIds.length === 0 ||
          (insurance.agency_clinic_id &&
            clinicIds.includes(insurance.agency_clinic_id))
      )
      .map(insurance => ({
        insurance,
        payer: payerByInsurance.get(insurance.id) ?? null,
        clinicName:
          clinicIds.length === 1
            ? null
            : (clinicNames.get(insurance.agency_clinic_id ?? '') ?? null),
      }))
      .filter(
        ({ insurance, payer }) =>
          !needle ||
          [
            insurance.name,
            insurance.edi_payer_id,
            payer?.name,
            ...(payer?.other_names ?? []),
          ]
            .filter(Boolean)
            .some(field => field?.toLowerCase().includes(needle))
      )
      .map(row => ({
        row,
        rank: row.payer
          ? summaryRank(
              row.payer,
              row.insurance.agency_clinic_id
                ? [row.insurance.agency_clinic_id]
                : [],
              transactions
            )
          : Number.MAX_SAFE_INTEGER,
      }))
      .sort((a, b) =>
        a.rank === b.rank
          ? a.row.insurance.name.localeCompare(b.row.insurance.name)
          : a.rank - b.rank
      )
      .map(({ row }) => row);
  }, [
    insurances,
    payerByInsurance,
    clinicNames,
    search,
    clinicIds,
    transactions,
  ]);

  // Every clinic's open steps, not narrowed by the search: a shared step
  // covers other clinics and payers too, and marking it done clears all.
  // Retired lines included, so stored work the badge counts stays reachable.
  const todos = useMemo(
    () => enrollmentTodos(payers, clinicIds, transactions),
    [payers, clinicIds, transactions]
  );

  // What the pane shows: a payer, or an insurance no payer pays. Only one the
  // table still shows, so a filter that hides it closes the pane. A clinic
  // line picks its payer and marks that clinic's insurance in the pane.
  const [stepsFor, setStepsFor] = useState<string | null>(null);
  const [markedClinic, setMarkedClinic] = useState<string | null>(null);
  const [stepsOpen, setStepsOpen] = useState(false);
  const select = (key: string, clinicId: string | null = null) => {
    setStepsFor(key);
    setMarkedClinic(clinicId);
    // Stacked, the pane is above the table: bring it into view.
    if (!pinned) {
      requestAnimationFrame(() =>
        paneRef.current?.scrollIntoView({ block: 'start' })
      );
    }
  };

  // The payers of the insurances in view, most urgent first, and the
  // insurances no payer pays at the bottom, as the table has always shown them.
  // Every payer with a clinic in view, whether or not an insurance links to
  // it: an enrollment Roger did not start, or one whose insurance was since
  // removed, still has to be visible, since the badge counts it.
  const { visiblePayers, retiredPayers } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matched = new Set(
      rows.flatMap(row => (row.payer ? [row.payer.key] : []))
    );
    const visible = (list: PayerEnrollmentGroup[]) =>
      list
        .filter(
          payer =>
            clinicIds.length === 0 ||
            (payer.clinics ?? []).some(line =>
              clinicIds.includes(line.agency_clinic_id)
            )
        )
        .filter(
          payer =>
            matched.has(payer.key) ||
            (!!needle &&
              [payer.name, payer.primary_payer_id, ...(payer.other_names ?? [])]
                .filter(Boolean)
                .some(field => field?.toLowerCase().includes(needle))) ||
            (!needle && linesInView(payer, clinicIds).length > 0)
        )
        .map(payer => ({
          payer,
          rank: summaryRank(payer, clinicIds, transactions),
        }))
        .sort((a, b) =>
          a.rank === b.rank
            ? a.payer.name.localeCompare(b.payer.name)
            : a.rank - b.rank
        )
        .map(({ payer }) => payer);
    const shown = visible(livePayers);
    const shownKeys = new Set(shown.map(payer => payer.key));
    return {
      visiblePayers: shown,
      retiredPayers: visible(retiredParts).filter(
        payer => !shownKeys.has(payer.key)
      ),
    };
  }, [livePayers, retiredParts, rows, search, clinicIds, transactions]);
  const allUnlinked = useMemo(
    () =>
      rows
        .filter(row => !row.payer)
        .map(row => ({
          id: row.insurance.id,
          name: row.insurance.name,
          edi_payer_id: row.insurance.edi_payer_id,
          agency_clinic_id: row.insurance.agency_clinic_id,
          clinic_name: row.clinicName,
          retired_in_wellsky: row.insurance.retired_in_wellsky,
        })),
    [rows]
  );
  const unlinked = allUnlinked.filter(
    insurance => !insurance.retired_in_wellsky
  );
  const retiredUnlinked = allUnlinked.filter(
    insurance => insurance.retired_in_wellsky
  );
  const openPayer =
    [...visiblePayers, ...retiredPayers].find(
      payer => payer.key === stepsFor
    ) ?? null;
  const openUnlinked = openPayer
    ? null
    : (rows.find(row => !row.payer && row.insurance.id === stepsFor) ?? null);
  // The payer's insurances in view, one per clinic line.
  const openInsurances = openPayer
    ? rows.filter(row => row.payer?.key === openPayer.key)
    : [];
  // The payer's insurances grouped by clinic, the clinics in the table's
  // order; any not tied to a clinic at the end.
  const openInsuranceGroups = openPayer
    ? [
        ...linesInView(openPayer, clinicIds).map(line => ({
          clinicId: line.agency_clinic_id,
          clinicName: clinicLabel(line.clinic_name),
          rows: openInsurances.filter(
            row => row.insurance.agency_clinic_id === line.agency_clinic_id
          ),
        })),
        {
          clinicId: '',
          clinicName: 'No clinic',
          rows: openInsurances.filter(row => !row.insurance.agency_clinic_id),
        },
      ].filter(group => group.rows.length > 0)
    : [];
  const openTodos = openPayer
    ? todos.filter(todo =>
        todo.steps.some(step => step.payerKey === openPayer.key)
      )
    : [];
  const openTitle = openPayer ? agencyTitle(openPayer, clinicIds) : null;

  // Pinned against the page's own scroll area, not the window: the app's top
  // bar sits outside it and its padding would push the pane down. So the pane
  // is placed and sized from that area, measured, with a small margin.
  const paneRef = useRef<HTMLDivElement>(null);
  // Sized to the part of the screen it has: before it pins, from where it
  // starts to the bottom, so its own scroll never leaves anything off screen
  // and always works.
  const [visibleHeight, setVisibleHeight] = useState<number | null>(null);
  const [pinned, setPinned] = useState<{
    top: number;
    maxHeight: number;
  } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      let area = paneRef.current?.parentElement ?? null;
      while (area && !/(auto|scroll)/.test(getComputedStyle(area).overflowY)) {
        area = area.parentElement;
      }
      if (!window.matchMedia('(min-width: 1024px)').matches) {
        setPinned(null);
        return;
      }
      const margin = 16;
      // No scroll area of its own means the window scrolls.
      const padding = area
        ? parseFloat(getComputedStyle(area).paddingTop) || 0
        : 0;
      setPinned({
        top: margin - padding,
        maxHeight: (area?.clientHeight ?? window.innerHeight) - margin * 2,
      });
    };
    let area = paneRef.current?.parentElement ?? null;
    while (area && !/(auto|scroll)/.test(getComputedStyle(area).overflowY)) {
      area = area.parentElement;
    }
    const scroller: HTMLElement | Window = area ?? window;
    const checkPinned = () => {
      const pane = paneRef.current;
      if (!pane) return;
      const areaBottom = area
        ? area.getBoundingClientRect().bottom
        : window.innerHeight;
      // The real space left, unclamped: a floor would push the pane's
      // bottom off screen again whenever less than that remains.
      setVisibleHeight(
        Math.max(0, areaBottom - pane.getBoundingClientRect().top - 16)
      );
    };
    // A resize moves both the pane's top and the area's bottom.
    const onResize = () => {
      measure();
      checkPinned();
    };
    measure();
    checkPinned();
    window.addEventListener('resize', onResize);
    scroller.addEventListener('scroll', checkPinned, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      scroller.removeEventListener('scroll', checkPinned);
    };
  }, [data, stepsFor]);

  const openStatus = useMemo(() => {
    if (!openPayer) return [];
    const states = new Map(
      transactionStates(openPayer, clinicIds, transactions).map(item => [
        item.transaction,
        item.state,
      ])
    );
    return transactions.map(transaction => {
      const support = openPayer[transaction]?.support;
      const state = states.get(transaction) ?? null;
      return { transaction, support, state, label: markLabel(support, state) };
    });
  }, [openPayer, clinicIds, transactions]);

  return (
    // Fills the Admin pane on desktop, so the payer table scrolls, not the page.
    <div className='space-y-4 lg:flex lg:h-full lg:flex-col'>
      {blindSpots.length > 0 && (
        <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
          <p>Some enrollment status can&apos;t be checked:</p>
          <ul className='mt-1 list-disc space-y-0.5 pl-5'>
            {blindSpots.map(part => (
              <li key={part}>{part}</li>
            ))}
          </ul>
        </div>
      )}

      <EnrollmentFilters
        search={search}
        onSearchChange={setSearch}
        clinics={clinics}
        selectedClinicIds={clinicIds}
        onClinicIdsChange={setClinicIds}
        selectedTransactions={selectedTransactions}
        onTransactionsChange={setSelectedTransactions}
        actions={
          // Two buttons and a menu: adding an insurance and starting
          // enrollments are the jobs; refreshing (the sync runs hourly on its
          // own), copying insurances between clinics and the recurring
          // eligibility opt-in are occasional.
          <div className='flex items-center gap-2'>
            {onAddInsurance && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A span, so the tooltip still shows on a disabled button. */}
                    <span>
                      <Button
                        variant='outline'
                        onClick={onAddInsurance}
                        disabled={Boolean(addInsuranceBlockedReason)}
                      >
                        <Plus className='mr-2 h-4 w-4' />
                        Add insurance
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {addInsuranceBlockedReason && (
                    <TooltipContent>{addInsuranceBlockedReason}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            {isSuperUser && !missingClinicAccess && (
              <Button onClick={() => setStartOpen(true)}>
                Start enrollments
              </Button>
            )}
            {((canRefresh && !missingClinicAccess) ||
              onCopyInsurances ||
              isSuperUser) && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' aria-label='More actions'>
                    {isRefreshing ? (
                      <RefreshCw className='h-4 w-4 animate-spin' />
                    ) : (
                      <MoreHorizontal className='h-4 w-4' />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-64'>
                  {canRefresh && !missingClinicAccess && (
                    <DropdownMenuItem
                      onClick={() => void handleRefresh()}
                      disabled={isRefreshing}
                      className='items-start'
                    >
                      <RefreshCw className='mr-2 mt-0.5 h-4 w-4 shrink-0' />
                      <div>
                        <div>Refresh enrollments now</div>
                        <div className='text-xs text-muted-foreground'>
                          Updates on its own every hour.
                        </div>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {onCopyInsurances && (
                    <DropdownMenuItem onClick={onCopyInsurances}>
                      <Copy className='mr-2 h-4 w-4' />
                      Copy insurances to clinic…
                    </DropdownMenuItem>
                  )}
                  {isSuperUser && (
                    <DropdownMenuItem onClick={() => setRecurringOpen(true)}>
                      <Repeat className='mr-2 h-4 w-4' />
                      Recurring eligibility checks…
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        }
      />

      {isLoading && !data ? (
        <p className='text-sm text-muted-foreground'>Loading enrollments…</p>
      ) : missingClinicAccess ? (
        <div className='rounded-md border py-8 text-center'>
          <p className='text-sm text-muted-foreground'>
            {NO_CLINIC_ACCESS_MESSAGE}
          </p>
        </div>
      ) : loadFailed ? (
        <div className='space-y-3 rounded-md border py-8 text-center'>
          <p className='text-sm text-muted-foreground'>
            {data
              ? 'Couldn’t check for changes, so this may be out of date.'
              : 'Couldn’t load enrollments.'}
          </p>
          <Button onClick={() => void load()} disabled={isLoading} size='sm'>
            Try again
          </Button>
        </div>
      ) : (
        // Half and half: the table to pick from, the payer picked beside it.
        // Stacked below lg, with the payer first once one is picked.
        <div className='flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:flex-row lg:items-start'>
          <div className='min-w-0 lg:h-full lg:w-3/5'>
            <PayerEnrollmentsTable
              payers={visiblePayers}
              noPayerInsurances={unlinked}
              retiredPayers={retiredPayers}
              retiredInsurances={retiredUnlinked}
              clinicIds={clinicIds}
              transactions={transactions}
              selectedKey={openPayer?.key ?? null}
              onSelect={key => select(key)}
              selectedLine={
                openPayer && markedClinic
                  ? { payerKey: openPayer.key, clinicId: markedClinic }
                  : null
              }
              onSelectLine={(payerKey, clinicId) => select(payerKey, clinicId)}
              selectedInsuranceId={openUnlinked?.insurance.id ?? null}
              onSelectInsurance={id => select(id)}
            />
          </div>
          <div
            ref={paneRef}
            style={
              pinned
                ? {
                    top: pinned.top,
                    maxHeight: Math.min(
                      pinned.maxHeight,
                      visibleHeight ?? pinned.maxHeight
                    ),
                    overflowY: 'auto',
                  }
                : undefined
            }
            className={`min-w-0 lg:sticky lg:w-2/5 ${
              openPayer || openUnlinked
                ? 'order-first rounded-md border lg:order-none'
                : 'hidden lg:block'
            }`}
          >
            {openPayer ? (
              <div className='p-4'>
                {/* The name ends in "…"; the count stays whole. */}
                <h2
                  className='flex text-lg font-semibold'
                  title={openTitle?.all.join(', ')}
                >
                  <PayerName title={openTitle} fallback={openPayer.name} />
                </h2>
                <p className='text-sm text-muted-foreground'>
                  {openTitle ? `Paid by ${openPayer.name} · ` : ''}Payer ID{' '}
                  {openPayer.primary_payer_id ?? '—'}
                </p>

                {openTodos.length > 0 ? (
                  // The work is a task of its own, done in a dialog; here it
                  // only has to be impossible to miss.
                  <Button
                    className='mt-4 w-full bg-red-600 text-white hover:bg-red-700'
                    onClick={() => setStepsOpen(true)}
                  >
                    <CircleAlert className='mr-2 h-4 w-4' />
                    Complete enrollment steps ({openTodos.length})
                  </Button>
                ) : (
                  // Nothing to do, so where it stands, in words.
                  <div className='mt-4 text-sm'>
                    {/* The marks carry tooltips, which need a provider here too. */}
                    <TooltipProvider delayDuration={200}>
                      <ul className='divide-y border-y'>
                        {openStatus.map(
                          ({ transaction, label, support, state }) => (
                            <li
                              key={transaction}
                              className='flex items-center gap-3 py-2'
                            >
                              <SupportIcon support={support} state={state} />
                              <span className='w-24 shrink-0 font-medium'>
                                {PAYER_TRANSACTION_LABELS[transaction]}
                              </span>
                              <span className='text-muted-foreground'>
                                {label}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </TooltipProvider>
                  </div>
                )}

                <section className='mt-6'>
                  <h3 className='border-b pb-1.5 text-sm font-semibold'>
                    Insurances
                  </h3>
                  {openInsurances.length === 0 && (
                    <p className='py-2 text-sm text-muted-foreground'>
                      None of your insurances are linked to this payer.
                    </p>
                  )}
                  {/* By clinic, in the order the table lists them. */}
                  {openInsuranceGroups.map(group => (
                    <div
                      key={group.clinicId}
                      // Same box either way, so picking a clinic only
                      // shades it; nothing moves.
                      className={`-mx-2 rounded-md px-2 pb-1 pt-2 ${
                        markedClinic && group.clinicId === markedClinic
                          ? 'bg-muted'
                          : ''
                      }`}
                    >
                      <div className='text-xs text-muted-foreground'>
                        {group.clinicName}
                      </div>
                      <ul className='divide-y pl-3'>
                        {group.rows.map(row => (
                          <InsuranceLine
                            key={row.insurance.id}
                            insurance={row.insurance}
                            clinicName={null}
                            wellSkySynced={isWellSkySyncedInsurance(
                              row.insurance,
                              agencyKinnserEnabled
                            )}
                            highlighted={false}
                            onEdit={onEdit}
                            onEditRates={onEditRates}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>

                <Dialog open={stepsOpen} onOpenChange={setStepsOpen}>
                  <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-2xl'>
                    <DialogHeader>
                      <DialogTitle>
                        {openTitle?.title ?? openPayer.name}
                      </DialogTitle>
                      <DialogDescription>Enrollment steps</DialogDescription>
                    </DialogHeader>
                    {openTodos.length > 0 ? (
                      <PayerSteps
                        payerKey={openPayer.key}
                        todos={openTodos}
                        onChanged={() => void load()}
                      />
                    ) : (
                      <p className='text-sm text-muted-foreground'>
                        All done. Nothing left for your agency to do here.
                      </p>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            ) : openUnlinked ? (
              <div className='p-4'>
                <InsuranceHeader
                  insurance={openUnlinked.insurance}
                  wellSkySynced={isWellSkySyncedInsurance(
                    openUnlinked.insurance,
                    agencyKinnserEnabled
                  )}
                  clinicName={
                    clinicNames.get(
                      openUnlinked.insurance.agency_clinic_id ?? ''
                    ) ?? null
                  }
                  onEdit={onEdit}
                  onEditRates={onEditRates}
                />
                <p className='mt-4 text-sm text-muted-foreground'>
                  {openUnlinked.insurance.edi_payer_id
                    ? 'No payer matches this payer ID, so there is no enrollment to track.'
                    : 'Add a payer ID to track this insurance’s enrollment.'}
                </p>
              </div>
            ) : (
              <div className='rounded-md border border-dashed py-12 text-center text-sm text-muted-foreground'>
                Select a payer to see its insurances and enrollment.
              </div>
            )}
          </div>
        </div>
      )}
      {isSuperUser && (
        <>
          <StartEnrollmentsDialog
            open={startOpen}
            onOpenChange={setStartOpen}
            clinicIds={clinicIds}
            clinicNames={clinicNames}
            payers={payers}
            // Stedi moves most new enrollments to Provider action required
            // within seconds, after the create reply was stored.
            onSubmitted={() => void handleStarted()}
          />
          <RecurringEligibilityDialog
            open={recurringOpen}
            onOpenChange={setRecurringOpen}
          />
        </>
      )}
    </div>
  );
}
